import React from "react";
import { Header, Table, Label, Icon } from "semantic-ui-react";
import { Student } from "../types";
import { formatGradeWithSuffix } from "../../../app/helpers/gradeHelpers";
import StudentEditModal from "./StudentEditModal";
import StudentActivationButton from "./StudentActivationButton";
import { useTranslation } from "react-i18next";
import agent from "../../../app/api/agent";
import { downloadFile } from "../../../app/helpers/fileHelper";

interface StudentListItemProps {
  student: Student;
  onStudentUpdate: () => void;
  surnameLetterElement: React.ReactNode;
  isLjaSchool: boolean;
}

const StudentListItem: React.FC<StudentListItemProps> = ({
  student,
  onStudentUpdate,
  surnameLetterElement,
  isLjaSchool,
}) => {
  const { t } = useTranslation();

  const downloadAgreement = async () => {
    const response = await agent.Files.getAgreement(student.agreement.file!);
    downloadFile(response);
  };

  const parentAgreementStatus = () => {
    const getStatusStyle = () => {
      switch (student.agreement.status) {
        case "approved":
          return { backgroundColor: "#00A0AF" };
        case "uploaded":
        case "virtualsigncompleted":
        case "virtualsignpending":
          return { backgroundColor: "#F39C12" };
        case "none":
        default:
          return { backgroundColor: "#EB5757" };
      }
    };

    const getStatusText = (): string => {
      switch (student.agreement.status) {
        case "approved":
          return t("studentAgreements.approved_short");
        case "uploaded":
        case "virtualsigncompleted":
          return t("studentAgreements.waiting_for_approval");
        case "virtualsignpending":
        case "none":
        default:
          return t("studentAgreements.not_uploaded_short");
      }
    };

    const getAgeStatusText = (): string => {
      if (student.agreement.isOver18 === true) {
        return t("studentAgreements.over_18");
      } else if (student.agreement.isOver18 === false) {
        return t("studentAgreements.under_18");
      } else {
        return t("studentAgreements.age_unknown");
      }
    };

    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Label size="small" style={getStatusStyle()}>
            <Icon name="file alternate outline" />
            {getStatusText()}
          </Label>
          {student.agreement?.file && (
            <Icon
              name="download"
              link
              title={t("applications.company.actions.download_agreement")}
              style={{ cursor: "pointer", color: "#666" }}
              onClick={downloadAgreement}
            />
          )}
        </div>
        <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>{getAgeStatusText()}</div>
      </>
    );
  };

  return (
    <>
      <React.Fragment key={student.id}>
        <Table.Row>
          <Table.Cell className="surname-letter">{surnameLetterElement}</Table.Cell>
          <Table.Cell>
            <Header as="h4">
              <Header.Content>
                {student.contactInfo.fullName}, {formatGradeWithSuffix(student.grade, student.isStudent)}
                <Header.Subheader>
                  <a href={"mailto:" + student.contactInfo.email}>{student.contactInfo.email}</a>
                  <br />
                  {isLjaSchool && <p style={{ fontSize: 13 }}>{student.school}</p>}
                </Header.Subheader>
              </Header.Content>
            </Header>
          </Table.Cell>
          <Table.Cell>
            <a href={"tel:" + student.contactInfo.phoneNumber}>{student.contactInfo.phoneNumber}</a>
          </Table.Cell>
          <Table.Cell>{parentAgreementStatus()}</Table.Cell>
          <Table.Cell className="student-list-actions" singleLine>
            <StudentEditModal student={student} onStudentUpdate={onStudentUpdate} />
            <StudentActivationButton student={student} onActivationChange={onStudentUpdate} showSimpleButton />
          </Table.Cell>
        </Table.Row>
      </React.Fragment>
    </>
  );
};

export default StudentListItem;
